import { useState } from 'react';
import "./styles.less"
import { useReportProperty } from './hook';
import { useTheme } from 'shared-components/Styles/ThemeHook';

type FormReportProps = {
    id: string | number;
    setModal: (show: boolean) => void;
}

const useFormValidation = (initialValues = {}) => {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});

  const validateField = (name, value, rules = []) => {
    let error = '';
    
    for (const rule of rules) {
      if (rule.required && (!value || (Array.isArray(value) && value.length === 0))) {
        error = rule.message || `${name} es requerido`;
        break;
      }
    }
    
    return error;
  };

  const validateFields = (fieldsConfig) => {
    return new Promise((resolve, reject) => {
      const newErrors = {};
      let hasErrors = false;

      Object.entries(fieldsConfig).forEach(([fieldName, config]) => {
        const fieldValue = values[fieldName];
        const fieldError = validateField(fieldName, fieldValue, config.rules);
        
        if (fieldError) {
          newErrors[fieldName] = fieldError;
          hasErrors = true;
        }
      });

      setErrors(newErrors);

      if (hasErrors) {
        reject(newErrors);
      } else {
        resolve(values);
      }
    });
  };

  const setFieldValue = (name, value) => {
    setValues(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  return {
    values,
    errors,
    setFieldValue,
    validateFields,
    setValues,
    setErrors
  };
};

export const FormReport = ({
    id,
    setModal
} : FormReportProps) => {
    const { options, reasons, sendReport } = useReportProperty({ id });
	const { theme } = useTheme();
    const form = useFormValidation();
  
    const fieldsConfig = {
        reasons: {
        rules: [
            {
            required: true,
            message: "Seleccione una razón para reportar la propiedad",
            },
        ]
        }
    };

    const handleClickShowNotification = async ({
		type = 'success',
		message = '',
		description = '',
	}) => {
		const { showNotification } = await import("shared-components/Components/CustomNotification/web");
		showNotification(
			type,
			message,
			description
		);
	}

    const handleCheckboxChange = (optionId) => {
        const currentReasons = form.values.reasons || [];
        let newReasons;

        if (currentReasons.includes(optionId)) {
            newReasons = currentReasons.filter(id => id !== optionId);
        } else {
            newReasons = [...currentReasons, optionId];
        }

        form.setFieldValue('reasons', newReasons);
        reasons.set(newReasons);
    };

    const handleSubmit = () => {
        form.validateFields(fieldsConfig)
        .then(data => {
            sendReport()
            .then(() => {
                setModal(false);
                handleClickShowNotification({
                    message: "Denuncia Enviada",
                    description: "La denuncia ha sido enviada correctamente. ",
                });
            })
            .catch(er => {
                setModal(false);
                handleClickShowNotification({
                    message: "Denuncia Cancelada",
                    description: "Hubo un error al enviar las denuncia.",
                    type: "error",
                });
            });
        })
        .catch(er => {
            // Los errores ya se muestran en el formulario
        });
    };

    return (
        <form onSubmit={(e) => e.preventDefault()}>
            <div className="form-field">
                <div className="checkbox-group">
                <div className="checkbox-options" style={{ display: 'grid', gap: theme.spacing.mdSpacing }}>
                    {options?.map(o => (
                    <div key={`report_option_${o["id"]}`} className="report-option checkbox-item">
                        <label className="checkbox-label">
                        <input
                            type="checkbox"
                            value={o["id"]}
                            checked={(form.values.reasons || []).includes(o["id"])}
                            onChange={() => handleCheckboxChange(o["id"])}
                            className="checkbox-input"
                        />
                        <span className="checkbox-text">{o["text"]}</span>
                        </label>
                    </div>
                    ))}
                </div>
                </div>
                {form.errors.reasons && (
                <div className="error-message" style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                    {form.errors.reasons}
                </div>
                )}
            </div>

            <div className="action-container">
                <button
                    className="button button-primary"
                    type="button"
                    onClick={handleSubmit}
                >
                    Reportar
                </button>
            </div>
        </form>
    );
};